import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Building2 } from 'lucide-react';
import { useFunderStore } from '../../store/useFunderStore';
import { useAppStore } from '../../store/useAppStore';
import type { FunderRelationshipStatus } from '../../types';

const STATUS_COLORS: Record<FunderRelationshipStatus, string> = {
  active: '#22c55e',
  prospect: '#3b82f6',
  inactive: '#94a3b8',
  former: '#6b7280',
};

const STATUS_LABELS: Record<FunderRelationshipStatus, string> = {
  active: 'Active',
  prospect: 'Prospect',
  inactive: 'Inactive',
  former: 'Former',
};

export default function FunderPanel() {
  const { funders } = useFunderStore();
  const { setActivePanel } = useAppStore();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);

  const filtered = funders.filter((f) => {
    const matchesSearch =
      f.name.toLowerCase().includes(search.toLowerCase()) ||
      (f.abbreviation || '').toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || f.relationshipStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleSelect = (id: string) => {
    setActivePanel(null);
    navigate(`/funder/${id}`);
  };

  const handleCreate = async (name: string, funderType: string) => {
    const { createFunder } = useFunderStore.getState();
    try {
      const funder = await createFunder({ name, funderType } as any);
      setShowCreate(false);
      setActivePanel(null);
      navigate(`/funder/${funder._id}`);
    } catch { /* toast handled in store */ }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 space-y-2">
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 w-full px-3 py-2 bg-accent text-white rounded-lg text-sm font-medium hover:bg-accent/90 transition-colors"
        >
          <Plus size={16} />
          New Funder
        </button>

        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search funders..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-1 focus:ring-accent"
        >
          <option value="all">All Statuses</option>
          {Object.entries(STATUS_LABELS).map(([id, label]) => (
            <option key={id} value={id}>{label}</option>
          ))}
        </select>
      </div>

      <div className="flex-1 overflow-y-auto px-2">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400 dark:text-gray-500">
            <Building2 size={32} className="mb-2" />
            <p className="text-sm">No funders found</p>
          </div>
        ) : (
          <div className="space-y-0.5">
            {filtered.map((funder) => (
              <button
                key={funder._id}
                onClick={() => handleSelect(funder._id)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-left transition-colors"
              >
                <div
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: STATUS_COLORS[funder.relationshipStatus] || '#94a3b8' }}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {funder.name}
                    {funder.abbreviation && <span className="text-gray-400 ml-1">({funder.abbreviation})</span>}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {funder.funderType} &middot; {STATUS_LABELS[funder.relationshipStatus] || funder.relationshipStatus}
                  </p>
                </div>
                {funder.stats && funder.stats.totalOpportunities > 0 && (
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    {funder.stats.totalOpportunities} opp
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {showCreate && (
        <QuickCreateFunder onClose={() => setShowCreate(false)} onCreate={handleCreate} />
      )}
    </div>
  );
}

function QuickCreateFunder({ onClose, onCreate }: { onClose: () => void; onCreate: (name: string, type: string) => void }) {
  const [name, setName] = useState('');
  const [funderType, setFunderType] = useState('other');

  return (
    <div className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-3 shadow-lg space-y-2">
      <input
        type="text" placeholder="Funder name *" value={name}
        onChange={(e) => setName(e.target.value)} autoFocus
        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent/50"
      />
      <select value={funderType} onChange={(e) => setFunderType(e.target.value)}
        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent/50">
        <option value="federal">Federal</option>
        <option value="state">State</option>
        <option value="local">Local</option>
        <option value="foundation">Foundation</option>
        <option value="corporate">Corporate</option>
        <option value="nonprofit">Nonprofit</option>
        <option value="international">International</option>
        <option value="other">Other</option>
      </select>
      <div className="flex gap-2">
        <button onClick={onClose} className="flex-1 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Cancel</button>
        <button onClick={() => name.trim() && onCreate(name.trim(), funderType)} className="flex-1 px-3 py-1.5 text-sm bg-accent text-white rounded-lg hover:bg-accent/90">Create</button>
      </div>
    </div>
  );
}
